import { _ } from "../../../core/polyglot/polyglot.js";
import {
	formatAsBimesterAndYear,
	formatAsHalfYearWithOptionalYear,
	formatAsMonthAndYear,
	formatAsQuarterAndYear,
	formatAsYear,
} from "../../../core/helpers/date-formatting-util.js";
import CustomPeriod from "../../period/CustomPeriod.js";
import Model from "../../Model.js";
import Month from "../../period/Month.js";
import nullPeriod from "../../period/nullPeriod.js";

/**
 * Abstract class for period templates.
 *
 * Period templates know how to find the period out of an interpretation
 * context.
 *
 * @param {string} [spec.periodType] - Type of the resulting period, e.g.
 *   Current, Month, YearToDate, etc. See {@link PeriodType}
 * @abstract
 */
export default class AbstractPeriodTemplate extends Model {
	constructor({ periodType = "Current" } = {}) {
		super(...arguments);

		this._periodType = periodType;
	}

	getPeriodType() {
		return this._periodType;
	}

	setPeriodType(newPeriodType) {
		this._periodType = newPeriodType;
	}

	accept(_visitor) {
		return this._subclassResponsibility();
	}

	getType() {
		return this.constructor.getType();
	}

	getPeriodForContext(_context) {
		return this._subclassResponsibility();
	}

	isFixedPeriodTemplate() {
		return false;
	}

	_makePeriod({ context, date, periodType }) {
		const fiscalYear = context.findYear((year) => year.includes(date));

		if (!fiscalYear) {
			return this._makeCustomPeriod({
				type: periodType,
				startDate: date,
				context,
			});
		}

		if (periodType === "ConsecutiveMonths") {
			return context.getPeriod().getEquivalentInFiscalYear(fiscalYear);
		}

		return (
			fiscalYear
				.asPeriods(periodType)
				.find((period) => period.includes(date)) ||
			this._makeCustomPeriod({
				type: periodType,
				startDate: date,
				context,
			})
		);
	}

	/**
	 * Make a custom period of `type`, starting at `startDate`.
	 */
	_makeCustomPeriod({ type, startDate, context }) {
		const formatters = {
			Bimester: formatAsBimesterAndYear,
			Quarter: formatAsQuarterAndYear,
			HalfYear: formatAsHalfYearWithOptionalYear,
			ConsecutiveMonths: formatAsMonthAndYear,
			Year: formatAsYear,
			Custom: () => _("Custom"),
		};

		if (type === "Month") {
			// this ensures the month is formatted using the Chart of Accounts language
			return this._makeCustomMonth({ startDate, context });
		}

		return new CustomPeriod({
			start: startDate,
			end: startDate,
			label: formatters[type](startDate),
			year: nullPeriod,
		});
	}

	_makeCustomMonth({ startDate, context }) {
		let smartString = "n/a";

		context.getChartOfAccounts().withLanguage(() => {
			const fakeMonth = new Month({ start: startDate, end: startDate });
			smartString = fakeMonth.toSmartString();
		});

		return new CustomPeriod({
			start: startDate,
			end: startDate,
			label: smartString,
			year: nullPeriod,
		});
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.periodTemplateType = this.getType();
		properties.periodType = this.getPeriodType();

		return properties;
	}
}
